import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  FiPlay, 
  FiStar, 
  FiShoppingCart,
  FiArrowLeft,
  FiCheck
} from 'react-icons/fi';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import toast from 'react-hot-toast';
import dashboardEvents from '../utils/dashboardEvents';

const CourseDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, refreshUser } = useAuth();
  const { addToCart } = useCart();
  
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEnrolled, setIsEnrolled] = useState(false);
  const [enrollmentProgress, setEnrollmentProgress] = useState(0);

  useEffect(() => {
    const fetchCourse = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`/api/courses/${id}`);
        
        // The API returns { course, isEnrolled, userProgress }
        const courseData = response.data.course || response.data;
        setCourse(courseData);
        
        // Check if user is enrolled (if authenticated)
        if (isAuthenticated && user) {
          const enrollment = user.enrolledCourses?.find(
            enrollment => enrollment.course._id === id || enrollment.course === id
          );
          if (enrollment) {
            setIsEnrolled(true);
            setEnrollmentProgress(enrollment.progress || 0);
          }
        }
      } catch (error) {
        console.error('Error fetching course:', error);
        toast.error('강의 정보를 불러오는데 실패했습니다');
      } finally {
        setLoading(false);
      }
    };

    fetchCourse();
    
    // Listen for enrollment events
    const handleEnrollmentAdded = async (data) => {
      const purchasedCourse = data.courses.find(course => course.courseId === id);
      if (purchasedCourse) {
        // Refresh user data to get updated enrollment status
        if (refreshUser) {
          await refreshUser();
        }
        setIsEnrolled(true);
        setEnrollmentProgress(0);
        toast.success('강의에 등록되었습니다!');
      }
    };

    // Listen for course purchase events
    const handleCoursePurchased = async (data) => {
      const purchasedCourse = data.courses.find(course => course.courseId === id);
      if (purchasedCourse) {
        // Refresh user data to get updated enrollment status
        if (refreshUser) {
          await refreshUser();
        }
        setIsEnrolled(true);
        setEnrollmentProgress(0);
        toast.success('강의 구매가 완료되었습니다!');
      }
    };
    
    dashboardEvents.on('enrollment-added', handleEnrollmentAdded);
    dashboardEvents.on('course-purchased', handleCoursePurchased);
    
    return () => {
      dashboardEvents.off('enrollment-added', handleEnrollmentAdded);
      dashboardEvents.off('course-purchased', handleCoursePurchased);
    };
  }, [id, isAuthenticated, user, refreshUser]);

  const handlePurchase = () => {
    if (!isAuthenticated) {
      // Navigate to login page with return URL
      navigate('/login', { state: { returnUrl: `/courses/${id}` } });
      return;
    }
    
    // Check if already enrolled
    if (isEnrolled) {
      toast.error('이미 등록된 강의입니다.');
      return;
    }
    
    // Ensure course is loaded and has required properties
    if (!course || !course._id || !course.title) {
      toast.error('강의 정보를 불러오는 중입니다. 잠시 후 다시 시도해주세요.');
      return;
    }
    
    // Ensure course has all required properties for cart
    const courseForCart = {
      _id: course._id,
      title: course.title,
      subtitle: course.subtitle || '',
      price: course.price || 0,
      originalPrice: course.originalPrice || course.price || 0,
      thumbnail: course.thumbnail || '/images/default-course.svg',
      totalDuration: course.totalDuration || 0,
      totalLessons: course.totalLessons || 0,
      createdBy: course.createdBy || { name: 'Unknown' },
      instructor: course.instructor || course.createdBy || { name: 'Unknown' }
    };
    
    // Add to cart and navigate to checkout
    const result = addToCart(courseForCart);
    if (result.success) {
      toast.success(result.message);
      navigate('/checkout');
    } else {
      toast.error(result.message);
    }
  };

  const handleContinueLearning = () => {
    // Get enrollment data to check for last viewed lesson
    const enrollment = user?.enrolledCourses?.find(
      enrollment => (enrollment.course?._id === id || enrollment.course === id)
    );
    
    // If there's a last viewed lesson, navigate to it
    if (enrollment?.lastViewedLesson) {
      const { unitIndex, lessonIndex } = enrollment.lastViewedLesson;
      navigate(`/courses/${id}/learn?unitIndex=${unitIndex}&lessonIndex=${lessonIndex}`);
    } else {
      navigate(`/courses/${id}/learn`);
    }
  };



  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="loading-spinner mx-auto"></div>
          <p className="mt-4 text-gray-600">강의 정보를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">강의를 찾을 수 없습니다</h2>
          <p className="text-gray-600">강의가 존재하지 않거나 삭제되었습니다.</p>
          <Link to="/courses" className="mt-4 inline-block text-primary-600 hover:text-primary-700">
            강의 목록으로 돌아가기
          </Link>
        </div>
      </div>
    );
  }

  const creator = course.createdBy || course.instructor;
  const creatorName = creator?.name || 'Unknown';
  const creatorAvatar = creator?.avatar || '/images/default-avatar.svg';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/courses')}
              className="text-gray-600 hover:text-gray-900 transition-colors"
            >
              <FiArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{course.title}</h1>
              <p className="text-gray-600">{course.subtitle}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Course Thumbnail */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              <img
                src={course.thumbnail || '/images/default-course.svg'}
                alt={course.title}
                className="w-full h-64 object-cover"
              />
            </div>

            {/* Course Description */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">강의 소개</h2>
              <div 
                className="prose max-w-none"
                dangerouslySetInnerHTML={{ __html: course.description || '강의 설명이 없습니다.' }}
              />
            </div>

            {/* Course Curriculum */}
            {course.units && course.units.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">강의 커리큘럼</h2>
                <div className="space-y-4">
                  {course.units.map((unit, unitIndex) => (
                    <div key={unitIndex} className="border border-gray-200 rounded-lg p-4">
                      <h3 className="font-medium text-gray-900 mb-2">
                        {unitIndex + 1}. {unit.title}
                      </h3>
                      <p className="text-gray-600 text-sm mb-3">{unit.description}</p>
                      {unit.lessons && unit.lessons.length > 0 && (
                        <div className="space-y-2">
                          {unit.lessons.map((lesson, lessonIndex) => (
                            <div key={lessonIndex} className="flex items-center space-x-2 text-sm text-gray-600">
                              <FiPlay className="w-3 h-3 text-gray-400" />
                              <span>{lesson.title}</span>

                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Requirements */}
            {course.requirements && course.requirements.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">선수 지식</h2>
                <ul className="space-y-2">
                  {course.requirements.map((requirement, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <FiCheck className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{requirement}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Learning Outcomes */}
            {course.learningOutcomes && course.learningOutcomes.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">학습 목표</h2>
                <ul className="space-y-2">
                  {course.learningOutcomes.map((outcome, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <FiCheck className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{outcome}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Instructor */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">강사 소개</h2>
              <div className="flex items-center space-x-4">
                <img
                  src={creatorAvatar}
                  alt={creatorName}
                  className="w-16 h-16 rounded-full"
                />
                <div>
                  <h3 className="text-lg font-medium text-gray-900">{creatorName}</h3>
                  <p className="text-gray-600">{creator?.bio || '강사 소개가 없습니다.'}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Course Info Card */}
            <div className="bg-white rounded-lg shadow-sm p-6 sticky top-8">
              <div className="space-y-4">
                {/* Price */}
                <div className="text-center">
                  <div className="text-3xl font-bold text-gray-900">
                    ₩{course.price?.toLocaleString() || '0'}
                  </div>
                  {course.originalPrice && course.originalPrice > course.price && (
                    <div className="text-lg text-gray-500 line-through">
                      ₩{course.originalPrice.toLocaleString()}
                    </div>
                  )}
                </div>

                {/* Course Stats */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">수강생</span>
                    <span className="font-medium">{course.enrolledStudents?.toLocaleString() || 0}명</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">강의 시간</span>
                    <span className="font-medium">{course.totalDuration || 0}시간</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">평점</span>
                    <div className="flex items-center space-x-1">
                      <FiStar className="w-4 h-4 text-yellow-400" />
                      <span className="font-medium">{course.averageRating || 0}</span>
                      <span className="text-gray-500">({course.totalReviews || 0})</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">난이도</span>
                    <span className="font-medium">{course.level || 'Beginner'}</span>
                  </div>
                </div>

                {/* Progress (if enrolled) */}
                {isEnrolled && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">진행률</span>
                      <span className="font-medium">{enrollmentProgress}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${enrollmentProgress}%` }}
                      ></div>
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="space-y-3">
                  {isEnrolled ? (
                    <button
                      onClick={handleContinueLearning}
                      className="w-full flex items-center justify-center space-x-2 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                    >
                      <FiPlay className="w-5 h-5" />
                      <span>계속 학습하기</span>
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={handlePurchase}
                        className="w-full flex items-center justify-center space-x-2 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                      >
                        <FiShoppingCart className="w-5 h-5" />
                        <span>{isAuthenticated ? '강의 구매하기' : '로그인 후 구매하기'}</span>
                      </button>
                      
                      {!isAuthenticated && (
                        <div className="text-center text-sm text-gray-600">
                          아직 계정이 없다면?{' '}
                          <Link to="/register" className="text-primary-600 hover:text-primary-700 underline">
                            회원가입
                          </Link>
                        </div>
                      )}
                    </>
                  )}
                  

                </div>

                {/* Course Features */}
                <div className="border-t border-gray-200 pt-4">
                  <h3 className="font-medium text-gray-900 mb-3">강의 특징</h3>
                  <div className="space-y-2 text-sm text-gray-600">
                    <div className="flex items-center space-x-2">
                      <FiCheck className="w-4 h-4 text-green-500" />
                      <span>평생 수강 가능</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <FiCheck className="w-4 h-4 text-green-500" />
                      <span>모바일 지원</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <FiCheck className="w-4 h-4 text-green-500" />
                      <span>수료증 발급</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <FiCheck className="w-4 h-4 text-green-500" />
                      <span>질문 및 답변</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CourseDetail; 